// client/src/features/audit/search.ts — SCR-AUD-001's URL state · AUD-002 (04 §9.2.3).
//
// §9.2.3 is normative for every LIST screen: filters and pagination live in the URL as
// Zod-validated search params, so a filtered journal is shareable, survives refresh, and Back
// is a filter-undo. `features/repairs/search.ts` is the shape this copies.
//
// The names are `GET /api/v1/audit-log`'s own — `entityType`, `entityId`, `userId`, `action`,
// `correlationId` plus 02 §7.4's paging — with ONE deliberate difference and TWO additions.
//
// THE DIFFERENCE IS THE TIME WINDOW. The endpoint takes `from`/`to` as UTC instants; the URL
// carries `de la`/`până la` as Europe/Bucharest BUSINESS DATES (`YYYY-MM-DD`), and `window.ts`
// converts. A shared link that said `from=2026-09-08T00:00:00Z` would mean „from 03:00
// Bucharest" to the person who opened it, silently hiding the first three hours of the day —
// and the audit log is the one screen where a silently missing row is the whole failure mode.
//
// THE TWO ADDITIONS ARE DISCLOSURE, NOT FILTERING. `highlight` names one row to scroll to,
// expand and mark — the deep link a notification or another screen sends here — and `drill`
// names the correlation id whose „toată cererea" dialog is open. Neither narrows the list:
// a highlighted row keeps its place among the others, because someone who followed a link
// still wants to see what happened around it.
//
// THERE IS NO `sort`. The server accepts the parameter and ignores it (D-1412): the journal is
// a chronology, newest first, and all four indexes of 03 §15.5 end in `timestamp DESC`. A sort
// control in the URL would be a promise the API does not keep.

import { z } from 'zod';

/** A Europe/Bucharest business date, the form `window.ts` converts to a UTC bound. */
const businessDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');

/** 02 §7.4 — `pageSize` default 25, max 200; the server refuses more rather than clamping. */
export const auditSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  /** The CLR entity name, as the trail stores it: `Contract`, `Expense`, `Auth`. Free text. */
  entityType: z.string().trim().optional(),
  entityId: z.string().uuid().optional(),
  /** The actor. Set by clicking an actor in the table — see `AuditJournal.tsx`. */
  userId: z.string().uuid().optional(),
  /** `Contract.Updated`, `Auth.LoginSucceeded`. Free text, for the reason `AuditActions` gives. */
  action: z.string().trim().optional(),
  correlationId: z.string().trim().optional(),
  from: businessDate.optional(),
  to: businessDate.optional(),
  /** The row a deep link named: scrolled into view, expanded, `aria-current`. */
  highlight: z.string().uuid().optional(),
  /** The correlation id whose drill dialog is open. */
  drill: z.string().trim().optional(),
});

export type AuditSearch = z.infer<typeof auditSearchSchema>;

/**
 * Which filters are engaged — the empty state needs to know why it is empty, and „nu s-a
 * întâmplat nimic încă" is a different sentence from „niciun rând nu se potrivește".
 *
 * `highlight` and `drill` are not filters and are not counted: neither removes a row.
 */
export function hasActiveFilters(search: AuditSearch): boolean {
  return (
    isSet(search.entityType) ||
    search.entityId !== undefined ||
    search.userId !== undefined ||
    isSet(search.action) ||
    isSet(search.correlationId) ||
    search.from !== undefined ||
    search.to !== undefined
  );
}

function isSet(value: string | undefined): boolean {
  return value !== undefined && value !== '';
}

/** Clearing the filters returns to page 1 and closes nothing the user opened. */
export function clearedSearch(search: AuditSearch): AuditSearch {
  const cleared: AuditSearch = { page: 1, pageSize: search.pageSize };
  if (search.highlight !== undefined) cleared.highlight = search.highlight;
  if (search.drill !== undefined) cleared.drill = search.drill;
  return cleared;
}
